import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, Routes, Route } from 'react-router-dom';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { saudiService } from '../../services/saudiService';
import MetricCard from '../../components/shared/MetricCard';
import PlotlyChart from '../../components/charts/PlotlyChart';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

/* ========== SHARED CONSTANTS & HELPERS ========== */
const COLORS = { Saudi: '#c9a96e', GCC: '#6baed6', Foreign: '#e07b54', buy: '#22c55e', sell: '#ef4444' };
const TYPE_COLORS = [
  '#c9a96e','#d4a853','#b8954f','#a6823a','#947026','#826012','#6f5000','#5d4000',
  '#6baed6','#4a9ec9','#2b8ebb',
  '#22c55e','#16a34a','#0d9488','#0891b2','#6366f1',
];
const LINE_COLORS = { Foreign: '#22c55e', Saudi: '#e07b54', GCC: '#6baed6' };
const LINE_DASH = { Foreign: 'solid', Saudi: 'dash', GCC: 'dot' };

const fmt = (val, suffix = '') => {
  if (val === null || val === undefined) return '-';
  const abs = Math.abs(val);
  if (abs >= 1e9) return `${(val / 1e9).toFixed(1)}B${suffix}`;
  if (abs >= 1e6) return `${(val / 1e6).toFixed(1)}M${suffix}`;
  if (abs >= 1e3) return `${(val / 1e3).toFixed(1)}K${suffix}`;
  return `${val.toFixed(0)}${suffix}`;
};
const fmtSAR = (v) => fmt(v, ' SAR');
const netColor = (v) => v == null ? undefined : v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
const cellColor = (v) => !v || v === 0 ? '' : v > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400';
const fmtCell = (v) => (v === 0 || v === undefined) ? '-' : fmt(v);

const fmtTick = (val) => {
  if (val === 0) return '0';
  const abs = Math.abs(val), sign = val < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(0)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${abs}`;
};

const buildYTicks = (dataArrays) => {
  let min = 0, max = 0;
  dataArrays.forEach(arr => arr.forEach(v => { if (v < min) min = v; if (v > max) max = v; }));
  const range = max - min || 1;
  const rawStep = range / 5;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const nice = [1, 2, 5, 10].find(n => n * mag >= rawStep) * mag;
  const tickvals = [];
  for (let v = Math.floor(min / nice) * nice; v <= max + nice * 0.5; v += nice) tickvals.push(v);
  return { tickvals, ticktext: tickvals.map(fmtTick) };
};

const wkLabel = (w) => new Date(w + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

/* ========== STANDARD PERIOD PILLS ========== */
const PERIODS = [
  { label: '1W', value: '1w' },
  { label: '1M', value: '1m' },
  { label: 'MTD', value: 'mtd' },
  { label: 'QTD', value: 'qtd' },
  { label: '3M', value: '3m' },
  { label: 'YTD', value: 'ytd' },
  { label: '1Y', value: '1y' },
  { label: 'ALL', value: 'all' },
];

const getStartDate = (period) => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  switch (period) {
    case '1w': { const dt = new Date(y, m, d - 7); return dt.toISOString().slice(0, 10); }
    case '1m': { const dt = new Date(y, m - 1, d); return dt.toISOString().slice(0, 10); }
    case 'mtd': return new Date(y, m, 1).toISOString().slice(0, 10);
    case 'qtd': { const qm = Math.floor(m / 3) * 3; return new Date(y, qm, 1).toISOString().slice(0, 10); }
    case '3m': { const dt = new Date(y, m - 3, d); return dt.toISOString().slice(0, 10); }
    case 'ytd': return `${y}-01-01`;
    case '1y': { const dt = new Date(y - 1, m, d); return dt.toISOString().slice(0, 10); }
    case 'all': return null;
    default: return null;
  }
};

const PeriodPills = ({ value, onChange }) => (
  <div className="flex gap-1 flex-wrap">
    {PERIODS.map(p => (
      <button key={p.value} onClick={() => onChange(p.value)}
        className={`text-[10px] px-2 py-1 rounded-md font-medium transition-colors ${
          value === p.value ? 'bg-[#c9a96e] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
        }`}
      >{p.label}</button>
    ))}
  </div>
);

const LevelToggle = ({ value, onChange, size }) => (
  <div className="flex gap-1">
    {[['nationality', 'By Nationality'], ['investor_type', 'By Investor Type']].map(([v, l]) => (
      <button key={v} onClick={() => onChange(v)}
        className={`text-[10px] px-2.5 py-1 rounded-md transition-colors ${value === v ? 'bg-[#c9a96e] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}
      >{l}</button>
    ))}
  </div>
);

const usePeriodParams = (defaultPeriod = 'ytd') => {
  const [period, setPeriod] = useState(defaultPeriod);
  const params = useMemo(() => {
    const sd = getStartDate(period);
    return sd ? { start_date: sd } : {};
  }, [period]);
  return { period, setPeriod, params };
};

/* ========== OVERVIEW ========== */
const OverviewView = ({ filterParams }) => {
  const { period, setPeriod, params } = usePeriodParams('ytd');
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLevel, setChartLevel] = useState('nationality');
  const [expanded, setExpanded] = useState({});

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        saudiService.getFlowsOverview(params),
        saudiService.getFlowsDetail(params),
      ]);
      setData(r1.data); setDetail(r2.data);
    } catch { setData(null); setDetail(null); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading overview..." />;
  if (!data) return <EmptyState />;

  const byNat = {};
  (data.by_nationality || []).forEach(r => { byNat[r.nationality] = r; });
  const totalTraded = data.total_traded || ((data.total_buy || 0) + (data.total_sell || 0));

  const weeklyByDate = {};
  (data.weekly || []).forEach(r => {
    if (!weeklyByDate[r.report_date]) weeklyByDate[r.report_date] = {};
    weeklyByDate[r.report_date][r.nationality] = r;
  });
  const weeks = Object.keys(weeklyByDate).sort();

  // Chart traces
  let chartTraces = [];
  if (chartLevel === 'nationality') {
    chartTraces = ['Foreign', 'Saudi', 'GCC'].map(nat => ({
      x: weeks, y: weeks.map(w => weeklyByDate[w]?.[nat]?.net || 0), name: `${nat} net`,
      type: 'scatter', mode: 'lines+markers',
      line: { color: LINE_COLORS[nat], width: 2.5, dash: LINE_DASH[nat] },
      marker: { size: 7 },
      hovertemplate: `${nat}: %{customdata}<extra></extra>`,
      customdata: weeks.map(w => fmtSAR(weeklyByDate[w]?.[nat]?.net || 0)),
    }));
  } else if (detail) {
    (detail.rows || []).forEach((row, i) => {
      const dw = detail.weeks || [];
      const vals = dw.map(w => row.weekly[w] || 0);
      chartTraces.push({
        x: dw, y: vals, name: `${row.nationality} - ${row.investor_type}`,
        type: 'scatter', mode: 'lines+markers',
        line: { color: TYPE_COLORS[i % TYPE_COLORS.length], width: 1.5 }, marker: { size: 4 },
        hovertemplate: `${row.nationality}/${row.investor_type}: %{customdata}<extra></extra>`,
        customdata: vals.map(v => fmtSAR(v)),
      });
    });
  }

  // Volume bars + TASI
  const totalVols = weeks.map(w => Object.values(weeklyByDate[w] || {}).reduce((s, r) => s + (r.buy || 0) + (r.sell || 0), 0));
  const volumeTrace = { x: weeks, y: totalVols, name: 'Total volume', type: 'bar', marker: { color: 'rgba(156,163,175,0.15)' }, yaxis: 'y2',
    hovertemplate: 'Volume: %{customdata}<extra></extra>', customdata: totalVols.map(v => fmtSAR(v)) };
  const tasiData = data.tasi || [];
  const tasiTrace = tasiData.length > 0 ? {
    x: tasiData.map(t => t.date), y: tasiData.map(t => t.close), name: 'TASI',
    type: 'scatter', mode: 'lines', line: { color: '#9ca3af', width: 1.5 },
    hovertemplate: 'TASI: %{y:,.0f}<extra></extra>', yaxis: 'y3',
  } : null;

  // Detail table
  const detailWeeks = detail?.weeks || [];
  const toggleExpand = (nat) => setExpanded(p => ({ ...p, [nat]: !p[nat] }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Period</span>
        <PeriodPills value={period} onChange={setPeriod} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Total value traded" value={fmtSAR(totalTraded)} />
        <MetricCard label="Foreign net flow" value={(byNat.Foreign?.net >= 0 ? '+' : '') + fmtSAR(byNat.Foreign?.net)} valueColor={netColor(byNat.Foreign?.net)} />
        <MetricCard label="Saudi net flow" value={(byNat.Saudi?.net >= 0 ? '+' : '') + fmtSAR(byNat.Saudi?.net)} valueColor={netColor(byNat.Saudi?.net)} />
        <MetricCard label="GCC net flow" value={(byNat.GCC?.net >= 0 ? '+' : '') + fmtSAR(byNat.GCC?.net)} valueColor={netColor(byNat.GCC?.net)} />
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Weekly Net Flow</h4>
          <LevelToggle value={chartLevel} onChange={setChartLevel} />
        </div>
        <PlotlyChart
          data={chartLevel === 'nationality' ? [volumeTrace, ...(tasiTrace ? [tasiTrace] : []), ...chartTraces] : chartTraces}
          layout={{
            xaxis: { type: 'date', tickfont: { size: 10 }, tickformat: '%b %d' },
            yaxis: { zeroline: true, zerolinecolor: 'rgba(150,150,150,0.3)', tickfont: { size: 10 }, ...buildYTicks(chartTraces.map(t => t.y)) },
            ...(chartLevel === 'nationality' ? {
              yaxis2: { overlaying: 'y', side: 'right', showgrid: false, visible: false, rangemode: 'tozero' },
              yaxis3: { overlaying: 'y', side: 'right', showgrid: false, tickformat: ',.0f', tickfont: { size: 10, color: '#9ca3af' } },
            } : {}),
            legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0, font: { size: chartLevel === 'nationality' ? 11 : 9 } },
            margin: { l: 60, r: 60, t: 30, b: 40 },
            shapes: [{ type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0, yref: 'y', line: { color: 'rgba(150,150,150,0.4)', width: 1, dash: 'dot' } }],
          }}
          style={{ minHeight: chartLevel === 'investor_type' ? 450 : 350 }}
        />
      </div>

      {detail && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Net Flows by Investor Type — click to expand</h4>
          </div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-[#0d1117] sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 sticky left-0 bg-gray-50 dark:bg-[#0d1117] min-w-[180px]">Category</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400 min-w-[80px]">Total Net</th>
                  {detailWeeks.map((w, i) => <th key={i} className="px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap min-w-[70px]">{wkLabel(w)}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {['Saudi', 'GCC', 'Foreign'].map(nat => {
                  const nt = detail.nationality_totals?.[nat] || {};
                  const rows = (detail.rows || []).filter(r => r.nationality === nat);
                  return (
                    <React.Fragment key={nat}>
                      <tr className="bg-gray-50/50 dark:bg-gray-800/30 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/60" onClick={() => toggleExpand(nat)}>
                        <td className="px-3 py-2 font-semibold text-gray-900 dark:text-gray-100 sticky left-0 bg-gray-50/50 dark:bg-gray-800/30 flex items-center gap-1">
                          {expanded[nat] ? <MdExpandLess className="text-sm" /> : <MdExpandMore className="text-sm" />}{nat}
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold tabular-nums ${cellColor(nt.total_net)}`}>{fmtCell(nt.total_net)}</td>
                        {detailWeeks.map((w, i) => <td key={i} className={`px-2 py-2 text-right tabular-nums font-medium ${cellColor(nt.weekly?.[w])}`}>{fmtCell(nt.weekly?.[w])}</td>)}
                      </tr>
                      {expanded[nat] && rows.map((row, ri) => (
                        <tr key={ri} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-1.5 pl-8 text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-[#161b22]">{row.investor_type}</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums ${cellColor(row.total_net)}`}>{fmtCell(row.total_net)}</td>
                          {detailWeeks.map((w, i) => <td key={i} className={`px-2 py-1.5 text-right tabular-nums ${cellColor(row.weekly?.[w])}`}>{fmtCell(row.weekly?.[w])}</td>)}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ========== NET FLOW TREND ========== */
const TrendView = ({ filterParams }) => {
  const { period, setPeriod, params } = usePeriodParams('ytd');
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLevel, setChartLevel] = useState('nationality');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([saudiService.getFlowsTrend(params), saudiService.getFlowsDetail(params)]);
      setData(r1.data); setDetail(r2.data);
    } catch { setData(null); setDetail(null); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading trend..." />;
  if (!data || !Array.isArray(data) || data.length === 0) return <EmptyState />;

  const byDate = {};
  data.forEach(r => { if (!byDate[r.report_date]) byDate[r.report_date] = {}; byDate[r.report_date][r.nationality] = r; });
  const weeks = Object.keys(byDate).sort();
  const wl = weeks.map(wkLabel);

  let netTraces = [], cumTraces = [];
  if (chartLevel === 'nationality') {
    ['Foreign', 'Saudi', 'GCC'].forEach(nat => {
      netTraces.push({ x: wl, y: weeks.map(w => byDate[w]?.[nat]?.net || 0), name: nat, type: 'scatter', mode: 'lines+markers',
        line: { color: LINE_COLORS[nat], width: 2 }, marker: { size: 5 },
        hovertemplate: `${nat}: %{customdata}<extra></extra>`, customdata: weeks.map(w => fmtSAR(byDate[w]?.[nat]?.net || 0)) });
      cumTraces.push({ x: wl, y: weeks.map(w => byDate[w]?.[nat]?.cumulative_net || 0), name: nat, type: 'scatter', mode: 'lines',
        line: { color: LINE_COLORS[nat], width: 2.5 }, fill: 'tozeroy', fillcolor: LINE_COLORS[nat] + '15',
        hovertemplate: `${nat}: %{customdata}<extra></extra>`, customdata: weeks.map(w => fmtSAR(byDate[w]?.[nat]?.cumulative_net || 0)) });
    });
  } else if (detail) {
    const dw = detail.weeks || [], dl = dw.map(wkLabel);
    (detail.rows || []).forEach((row, i) => {
      const vals = dw.map(w => row.weekly[w] || 0);
      const lbl = `${row.nationality} - ${row.investor_type}`;
      netTraces.push({ x: dl, y: vals, name: lbl, type: 'scatter', mode: 'lines+markers',
        line: { color: TYPE_COLORS[i % TYPE_COLORS.length], width: 1.5 }, marker: { size: 3 },
        hovertemplate: `${lbl}: %{customdata}<extra></extra>`, customdata: vals.map(fmtSAR) });
      let cum = 0;
      const cv = vals.map(v => { cum += v; return cum; });
      cumTraces.push({ x: dl, y: cv, name: lbl, type: 'scatter', mode: 'lines',
        line: { color: TYPE_COLORS[i % TYPE_COLORS.length], width: 1.5 },
        hovertemplate: `${lbl}: %{customdata}<extra></extra>`, customdata: cv.map(fmtSAR) });
    });
  }

  const netYT = buildYTicks(netTraces.map(t => t.y)), cumYT = buildYTicks(cumTraces.map(t => t.y));
  const zeroLine = [{ type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0, line: { color: '#666', width: 1, dash: 'dot' } }];
  const leg = { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0, font: { size: chartLevel === 'nationality' ? 11 : 9 } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Period</span>
        <PeriodPills value={period} onChange={setPeriod} />
      </div>
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Weekly Net Flow</h4>
          <LevelToggle value={chartLevel} onChange={setChartLevel} />
        </div>
        <PlotlyChart data={netTraces} layout={{ xaxis: { type: 'category', tickfont: { size: 10 } }, yaxis: { tickfont: { size: 10 }, ...netYT }, legend: leg, shapes: zeroLine }}
          style={{ minHeight: chartLevel === 'investor_type' ? 450 : 350 }} />
      </div>
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Cumulative Net Flow</h4>
          <LevelToggle value={chartLevel} onChange={setChartLevel} />
        </div>
        <PlotlyChart data={cumTraces} layout={{ xaxis: { type: 'category', tickfont: { size: 10 } }, yaxis: { tickfont: { size: 10 }, ...cumYT }, legend: leg, shapes: zeroLine }}
          style={{ minHeight: chartLevel === 'investor_type' ? 450 : 350 }} />
      </div>
    </div>
  );
};

/* ========== BREAKDOWN ========== */
const BreakdownView = ({ filterParams }) => {
  const { period, setPeriod, params } = usePeriodParams('ytd');
  const [allData, setAllData] = useState({}); // { Saudi: data, GCC: data, Foreign: data }
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNat, setSelectedNat] = useState('All');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g, f, d] = await Promise.all([
        saudiService.getFlowsBreakdown({ ...params, nationality: 'Saudi' }),
        saudiService.getFlowsBreakdown({ ...params, nationality: 'GCC' }),
        saudiService.getFlowsBreakdown({ ...params, nationality: 'Foreign' }),
        saudiService.getFlowsDetail(params),
      ]);
      setAllData({ Saudi: s.data, GCC: g.data, Foreign: f.data });
      setDetail(d.data);
    } catch { setAllData({}); setDetail(null); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading breakdown..." />;
  if (!allData.Saudi) return <EmptyState />;

  // Build bar chart data
  const natsToShow = selectedNat === 'All' ? ['Saudi', 'GCC', 'Foreign'] : [selectedNat];
  const barItems = [];
  natsToShow.forEach(nat => {
    (allData[nat]?.by_type || []).forEach(r => {
      barItems.push({ label: selectedNat === 'All' ? `${nat}` : r.investor_type, subLabel: selectedNat === 'All' ? '' : '', nat, ...r });
    });
  });

  // For "All" view: show nationality-level buy/sell/net
  let barTraces, barLabels;
  if (selectedNat === 'All') {
    barLabels = ['Saudi', 'GCC', 'Foreign'];
    const buys = barLabels.map(n => (allData[n]?.by_type || []).reduce((s, r) => s + r.buy, 0));
    const sells = barLabels.map(n => (allData[n]?.by_type || []).reduce((s, r) => s + r.sell, 0));
    const nets = barLabels.map((_, i) => buys[i] - sells[i]);
    barTraces = [
      { x: barLabels, y: buys, name: 'Buy', type: 'bar', marker: { color: '#22c55e' }, hovertemplate: '%{x} Buy: %{customdata}<extra></extra>', customdata: buys.map(fmtSAR) },
      { x: barLabels, y: sells, name: 'Sell', type: 'bar', marker: { color: '#ef4444' }, hovertemplate: '%{x} Sell: %{customdata}<extra></extra>', customdata: sells.map(fmtSAR) },
      { x: barLabels, y: nets, name: 'Net', type: 'bar', marker: { color: nets.map(v => v >= 0 ? '#3b82f6' : '#f97316') }, hovertemplate: '%{x} Net: %{customdata}<extra></extra>', customdata: nets.map(fmtSAR) },
    ];
  } else {
    const byType = allData[selectedNat]?.by_type || [];
    barLabels = byType.map(r => r.investor_type);
    barTraces = [
      { x: barLabels, y: byType.map(r => r.buy), name: 'Buy', type: 'bar', marker: { color: '#22c55e' }, hovertemplate: '%{x}<br>Buy: %{customdata}<extra></extra>', customdata: byType.map(r => fmtSAR(r.buy)) },
      { x: barLabels, y: byType.map(r => r.sell), name: 'Sell', type: 'bar', marker: { color: '#ef4444' }, hovertemplate: '%{x}<br>Sell: %{customdata}<extra></extra>', customdata: byType.map(r => fmtSAR(r.sell)) },
      { x: barLabels, y: byType.map(r => r.net), name: 'Net', type: 'bar', marker: { color: byType.map(r => r.net >= 0 ? '#3b82f6' : '#f97316') }, hovertemplate: '%{x}<br>Net: %{customdata}<extra></extra>', customdata: byType.map(r => fmtSAR(r.net)) },
    ];
  }

  // Heatmap — build from detail data for selected nationality or all
  const dWeeks = detail?.weeks || [];
  const dLabels = dWeeks.map(wkLabel);
  const heatRows = selectedNat === 'All'
    ? ['Saudi', 'GCC', 'Foreign'].map(nat => ({ label: nat, vals: dWeeks.map(w => detail?.nationality_totals?.[nat]?.weekly?.[w] || 0) }))
    : (detail?.rows || []).filter(r => r.nationality === selectedNat).map(r => ({ label: r.investor_type, vals: dWeeks.map(w => r.weekly[w] || 0) }));

  // TASI weekly returns % for heatmap
  const tasiData = detail?.weeks ? null : null; // We'll compute from overview if available

  // Summary table
  const summaryRows = selectedNat === 'All'
    ? ['Saudi', 'GCC', 'Foreign'].map(nat => {
        const bt = allData[nat]?.by_type || [];
        return { label: nat, buy: bt.reduce((s, r) => s + r.buy, 0), sell: bt.reduce((s, r) => s + r.sell, 0), net: bt.reduce((s, r) => s + r.net, 0) };
      })
    : (allData[selectedNat]?.by_type || []).map(r => ({ label: r.investor_type, buy: r.buy, sell: r.sell, net: r.net }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">View:</span>
          {['All', 'Saudi', 'GCC', 'Foreign'].map(nat => (
            <button key={nat} onClick={() => setSelectedNat(nat)}
              className={`text-xs px-3 py-1 rounded-md transition-colors ${selectedNat === nat ? 'bg-[#c9a96e] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
            >{nat}</button>
          ))}
        </div>
        <PeriodPills value={period} onChange={setPeriod} />
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
          {selectedNat === 'All' ? 'Buy / Sell / Net by Nationality' : `${selectedNat} — Buy / Sell / Net by Investor Type`}
        </h4>
        <PlotlyChart data={barTraces} layout={{
          barmode: 'group', xaxis: { type: 'category', tickangle: -20, tickfont: { size: 10 } },
          yaxis: { ...buildYTicks(barTraces.map(t => t.y)) },
          legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0 },
        }} />
      </div>

      {summaryRows.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-400">{selectedNat === 'All' ? 'Nationality' : 'Investor Type'}</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Buy</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Sell</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {summaryRows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-gray-800 dark:text-gray-200 font-medium">{r.label}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtSAR(r.buy)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtSAR(r.sell)}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-semibold ${r.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                      {r.net >= 0 ? '+' : ''}{fmtSAR(r.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {heatRows.length > 0 && dLabels.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Net Flow Heatmap</h4>
          <PlotlyChart interactive
            data={[{ z: heatRows.map(r => r.vals), x: dLabels, y: heatRows.map(r => r.label),
              type: 'heatmap', colorscale: [[0, '#ef4444'], [0.5, '#1a1a2e'], [1, '#22c55e']], zmid: 0,
              hovertemplate: '%{y}<br>%{x}<br>Net: %{z:,.0f} SAR<extra></extra>',
              colorbar: { title: 'Net SAR', titleside: 'right' } }]}
            layout={{ xaxis: { type: 'category', side: 'bottom', tickfont: { size: 10 } },
              yaxis: { type: 'category', autorange: 'reversed', tickfont: { size: 10 } },
              margin: { l: 140, r: 60, t: 10, b: 50 } }}
            style={{ minHeight: Math.max(200, heatRows.length * 30 + 80) }}
          />
        </div>
      )}
    </div>
  );
};

/* ========== COMPOSITION ========== */
const CompositionView = ({ filterParams }) => {
  const { period, setPeriod, params } = usePeriodParams('ytd');
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('nationality'); // nationality | investor_type

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([saudiService.getFlowsComposition(params), saudiService.getFlowsDetail(params)]);
      setData(r1.data); setDetail(r2.data);
    } catch { setData(null); setDetail(null); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading composition..." />;
  if (!data || !Array.isArray(data) || data.length === 0) return <EmptyState />;

  const byDate = {};
  data.forEach(r => { if (!byDate[r.report_date]) byDate[r.report_date] = {}; byDate[r.report_date][r.nationality] = r; });
  const weeks = Object.keys(byDate).sort();
  const wl = weeks.map(wkLabel);
  const nats = ['Saudi', 'GCC', 'Foreign'];

  // Nationality-level composition
  const buyTraces = nats.map(nat => ({
    x: wl, y: weeks.map(w => byDate[w]?.[nat]?.buy_pct || 0), name: nat, type: 'bar', marker: { color: COLORS[nat] },
    hovertemplate: `${nat}: %{y:.1f}%<extra></extra>`,
  }));
  const sellTraces = nats.map(nat => ({
    x: wl, y: weeks.map(w => byDate[w]?.[nat]?.sell_pct || 0), name: nat, type: 'bar', marker: { color: COLORS[nat] }, showlegend: false,
    hovertemplate: `${nat}: %{y:.1f}%<extra></extra>`,
  }));

  // Net flow composition: net buyers stack above 0, net sellers stack below 0
  // Each bar shows absolute SAR value, positive or negative, stacked by nationality
  const netTraces = nats.map(nat => {
    const vals = weeks.map(w => {
      const d = byDate[w];
      if (!d) return 0;
      return (d[nat]?.buy_value || 0) - (d[nat]?.sell_value || 0);
    });
    return {
      x: wl, y: vals, name: nat, type: 'bar',
      marker: { color: COLORS[nat] },
      hovertemplate: `${nat}: %{customdata}<extra></extra>`,
      customdata: vals.map(v => fmtSAR(v)),
    };
  });

  // Investor-type level traces
  let invNetTraces = [], invBuyTraces = [];
  if (view === 'investor_type' && detail) {
    const dw = detail.weeks || [], dl = dw.map(wkLabel);
    const allRows = detail.rows || [];

    // Net flow by investor type (stacked relative — buyers above, sellers below)
    invNetTraces = allRows.map((row, i) => {
      const vals = dw.map(w => row.weekly[w] || 0);
      return {
        x: dl, y: vals, name: `${row.nationality}-${row.investor_type}`, type: 'bar',
        marker: { color: TYPE_COLORS[i % TYPE_COLORS.length] },
        hovertemplate: `${row.nationality}/${row.investor_type}: %{customdata}<extra></extra>`,
        customdata: vals.map(fmtSAR),
      };
    });

    // Buy composition % by investor type
    invBuyTraces = allRows.map((row, i) => {
      const vals = dw.map(w => {
        const totalBuy = allRows.reduce((s, r) => s + Math.max(r.weekly[w] || 0, 0), 0);
        const v = Math.max(row.weekly[w] || 0, 0);
        return totalBuy > 0 ? (v / totalBuy * 100) : 0;
      });
      return { x: dl, y: vals, name: `${row.nationality}-${row.investor_type}`, type: 'bar',
        marker: { color: TYPE_COLORS[i % TYPE_COLORS.length] },
        hovertemplate: `${row.nationality}/${row.investor_type}: %{y:.1f}%<extra></extra>` };
    });
  }

  const stackLayout = { barmode: 'stack', xaxis: { type: 'category', tickfont: { size: 10 } },
    yaxis: { ticksuffix: '%', range: [0, 100] },
    legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0, font: { size: view === 'nationality' ? 11 : 8 } } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <LevelToggle value={view} onChange={setView} />
        <PeriodPills value={period} onChange={setPeriod} />
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Net Flow — Buyers Above, Sellers Below</h4>
        {(() => {
          const traces = view === 'investor_type' && invNetTraces.length > 0 ? invNetTraces : netTraces;
          return <PlotlyChart data={traces} layout={{
            barmode: 'relative',
            xaxis: { type: 'category', tickfont: { size: 10 } },
            yaxis: { ...buildYTicks(traces.map(t => t.y)), zeroline: true, zerolinewidth: 2, zerolinecolor: 'rgba(150,150,150,0.5)' },
            legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0, font: { size: view === 'investor_type' ? 8 : 11 } },
          }} style={{ minHeight: view === 'investor_type' ? 450 : 350 }} />;
        })()}
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Buy Composition (%)</h4>
        <PlotlyChart data={view === 'nationality' ? buyTraces : invBuyTraces} layout={stackLayout} />
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Sell Composition (%)</h4>
        <PlotlyChart data={sellTraces} layout={{ ...stackLayout }} />
      </div>
    </div>
  );
};

/* ========== EMPTY STATE ========== */
const EmptyState = () => (
  <div className="text-center py-16 text-gray-500 dark:text-gray-400">
    <div className="text-4xl mb-3 opacity-30">&#x1f4ca;</div>
    <p className="text-sm">No data available. Upload weekly reports to get started.</p>
  </div>
);

/* ========== MAIN ========== */
const subTabs = [
  { to: '/saudi/flows', label: 'Overview', end: true },
  { to: '/saudi/flows/trend', label: 'Net Flow Trend' },
  { to: '/saudi/flows/breakdown', label: 'Breakdown' },
  { to: '/saudi/flows/composition', label: 'Composition' },
];

const FlowsPage = ({ filterParams }) => (
  <div>
    <div className="flex overflow-x-auto gap-1 mb-4">
      {subTabs.map(tab => (
        <NavLink key={tab.to} to={tab.to} end={tab.end}
          className={({ isActive }) => `px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
            isActive ? 'bg-[#c9a96e]/10 text-[#c9a96e] border border-[#c9a96e]/30'
              : 'text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-[#c9a96e]/30'}`}
        >{tab.label}</NavLink>
      ))}
    </div>
    <Routes>
      <Route index element={<OverviewView filterParams={filterParams} />} />
      <Route path="trend" element={<TrendView filterParams={filterParams} />} />
      <Route path="breakdown" element={<BreakdownView filterParams={filterParams} />} />
      <Route path="composition" element={<CompositionView filterParams={filterParams} />} />
    </Routes>
  </div>
);

export default FlowsPage;
